// features/findings/charts/divergence-timeline.js
// Annotated divergence timeline — used as Form 2 (Pair comparison) hero.
// Two time-series on shared axes with the deviation point marked.

export function DivergenceTimeline({
  title,
  series,        // [{ id, label, color, points: [{x, y}] }, ...]
  splitPoint,    // { x, label } — the deviation point
  yLabel = 'value',
  xLabel = 'year',
  yMin,
  yMax,
}) {
  // Render condition: at least 1 series with ≥2 points each.
  // Originally designed for paired comparison (≥2 series) but Form 5 uses
  // single-trajectory with branch-point markers — both shapes are valid.
  if (!series || series.length < 1 || series.some((s) => !s.points || s.points.length < 2)) {
    return null;
  }

  const allPoints = series.flatMap((s) => s.points);
  const xs = allPoints.map((p) => p.x);
  const ys = allPoints.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = yMin ?? Math.min(...ys);
  const maxY = yMax ?? Math.max(...ys);

  // SVG viewBox
  const W = 720;
  const H = 320;
  const ML = 56; // left margin (y-axis labels)
  const MR = 80; // right margin (series end labels)
  const MT = 24;
  const MB = 44;
  const innerW = W - ML - MR;
  const innerH = H - MT - MB;

  const xScale = (x) => ML + ((x - minX) / (maxX - minX)) * innerW;
  const yScale = (y) => MT + ((maxY - y) / (maxY - minY)) * innerH;

  // Build axis ticks
  const yTickCount = 5;
  const yTicks = Array.from({ length: yTickCount }, (_, i) => {
    const v = minY + ((maxY - minY) * i) / (yTickCount - 1);
    return Number(v.toFixed(2));
  });
  const yearSpan = maxX - minX;
  const xTickStep = yearSpan > 30 ? 5 : yearSpan > 15 ? 2 : 1;
  const xTicks = [];
  for (let y = Math.ceil(minX / xTickStep) * xTickStep; y <= maxX; y += xTickStep) {
    xTicks.push(y);
  }

  return (
    <figure className="finding-chart">
      <figcaption className="finding-chart-caption">{title}</figcaption>
      <div className="finding-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="finding-svg" role="img" aria-label={title}>
          {/* Y-axis grid lines + labels */}
          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={ML} y1={yScale(t)} x2={W - MR} y2={yScale(t)}
                className="finding-svg-grid"
              />
              <text x={ML - 8} y={yScale(t)} className="finding-svg-axis-label" textAnchor="end" dominantBaseline="middle">
                {t}
              </text>
            </g>
          ))}
          {/* X-axis */}
          <line x1={ML} y1={H - MB} x2={W - MR} y2={H - MB} className="finding-svg-axis" />
          {xTicks.map((t) => (
            <g key={t}>
              <line x1={xScale(t)} y1={H - MB} x2={xScale(t)} y2={H - MB + 4} className="finding-svg-axis" />
              <text x={xScale(t)} y={H - MB + 18} className="finding-svg-axis-label" textAnchor="middle">
                {t}
              </text>
            </g>
          ))}
          {/* Y-axis label */}
          <text x={12} y={MT + innerH / 2} className="finding-svg-axis-title" textAnchor="middle"
                transform={`rotate(-90 12 ${MT + innerH / 2})`}>
            {yLabel}
          </text>
          {/* X-axis label */}
          <text x={ML + innerW / 2} y={H - 8} className="finding-svg-axis-title" textAnchor="middle">
            {xLabel}
          </text>

          {/* Split-point vertical line(s) — accept single OR array */}
          {splitPoint && !Array.isArray(splitPoint) && (
            <g>
              <line
                x1={xScale(splitPoint.x)} y1={MT}
                x2={xScale(splitPoint.x)} y2={H - MB}
                className="finding-svg-split"
              />
              <text
                x={xScale(splitPoint.x) + 6} y={MT + 14}
                className="finding-svg-split-label"
              >
                {splitPoint.label}
              </text>
            </g>
          )}
          {Array.isArray(splitPoint) && splitPoint.map((sp, i) => {
            const tone = sp.tone || 'brass';
            // Stagger labels vertically so multiple branch points don't collide
            const labelY = MT + 14 + (i % 3) * 14;
            return (
              <g key={`sp-${sp.x}-${i}`}>
                <line
                  x1={xScale(sp.x)} y1={MT}
                  x2={xScale(sp.x)} y2={H - MB}
                  className={`finding-svg-split finding-svg-split-${tone}`}
                />
                <text
                  x={xScale(sp.x) + 6} y={labelY}
                  className={`finding-svg-split-label finding-svg-split-label-${tone}`}
                >
                  {sp.label}
                </text>
              </g>
            );
          })}

          {/* Series lines */}
          {series.map((s) => {
            const d = s.points.map((p, i) =>
              `${i === 0 ? 'M' : 'L'} ${xScale(p.x)} ${yScale(p.y)}`
            ).join(' ');
            const last = s.points[s.points.length - 1];
            return (
              <g key={s.id}>
                <path d={d} className={`finding-svg-line finding-svg-line-${s.color}`} />
                {/* End label */}
                <text
                  x={xScale(last.x) + 8}
                  y={yScale(last.y)}
                  className={`finding-svg-series-label finding-svg-series-${s.color}`}
                  dominantBaseline="middle"
                >
                  {s.label}
                </text>
                {/* Endpoint dot */}
                <circle cx={xScale(last.x)} cy={yScale(last.y)} r={3.5}
                  className={`finding-svg-dot finding-svg-dot-${s.color}`} />
              </g>
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

export default DivergenceTimeline;
